import { useTranslation } from 'react-i18next';

import {
  filterSelectableLeafIds,
  isTableRowCheckboxDisabled,
} from '@/components/ui-v2/table-checkbox-utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { lastClickedRowState } from '@/states/last-clicked-row.state';
import {
  applyRangeSelection,
  getRowSelectionStatus,
  rowSelectionModelState,
  selectedProductsCountState,
  toggleSingleRow,
} from '@/states/row-selection.state';
import { Row, Table } from '@tanstack/react-table';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

interface CheckboxCellProps {
  row: Row<DealItemWithVersions>;
  table: Table<DealItemWithVersions>;
  isOfferLocked: boolean;
  setLastClickedRow: ReturnType<typeof useSetAtom<typeof lastClickedRowState>>;
  store: ReturnType<typeof useStore>;
}

function CheckboxCell({
  row,
  table,
  isOfferLocked,
  setLastClickedRow,
  store,
}: CheckboxCellProps) {
  const hasSubRows = row.subRows.length > 1;
  const selectedProductsCount = useAtomValue(selectedProductsCountState);
  const model = useAtomValue(rowSelectionModelState);
  const setModel = useSetAtom(rowSelectionModelState);
  const { t } = useTranslation();

  const isCheckboxDisabled = isTableRowCheckboxDisabled(
    store,
    row,
    isOfferLocked
  );

  const checkedStatus = getRowSelectionStatus(model, row);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isOfferLocked || isCheckboxDisabled) {
      return;
    }

    // Read fresh values from the store to avoid stale closures
    const currentModel = store.get(rowSelectionModelState);
    const currentSelectionState =
      getRowSelectionStatus(currentModel, row) === true;
    const lastClickedRow = store.get(lastClickedRowState);

    if (!event.shiftKey || lastClickedRow === null) {
      if (hasSubRows) {
        // Toggle all leaf rows for multi-variant parent rows
        const leafIds = row.getLeafRows().map((lr) => lr.original.id);
        const selecting = !currentSelectionState;
        const idsToApply = selecting
          ? filterSelectableLeafIds(store, leafIds)
          : leafIds;
        if (selecting && idsToApply.length === 0) return;
        setModel(applyRangeSelection(currentModel, idsToApply, selecting));
      } else {
        // Use original.id (not row.id which may be a group key for parent rows)
        setModel(toggleSingleRow(currentModel, row.original.id));
      }
      setLastClickedRow({
        rowId: row.id,
        wasSelected: !currentSelectionState,
      });
      return;
    }

    // Shift-click range selection logic
    const allRows = table.getRowModel().rows;

    const lastClickedIndex = allRows.findIndex(
      (r) => r.id === lastClickedRow.rowId
    );
    const currentIndex = allRows.findIndex((r) => r.id === row.id);

    if (lastClickedIndex === -1 || currentIndex === -1) {
      if (hasSubRows) {
        const leafIds = row.getLeafRows().map((lr) => lr.original.id);
        const selecting = !currentSelectionState;
        const idsToApply = selecting
          ? filterSelectableLeafIds(store, leafIds)
          : leafIds;
        if (selecting && idsToApply.length === 0) return;
        setModel(applyRangeSelection(currentModel, idsToApply, selecting));
      } else {
        setModel(toggleSingleRow(currentModel, row.original.id));
      }
      setLastClickedRow({
        rowId: row.id,
        wasSelected: !currentSelectionState,
      });
      return;
    }

    const startIndex = Math.min(lastClickedIndex, currentIndex);
    const endIndex = Math.max(lastClickedIndex, currentIndex);
    const targetState = lastClickedRow.wasSelected;

    // Collect product IDs from the range, expanding multi-variant parents to leaf IDs
    const rangeIds: string[] = [];
    allRows.slice(startIndex, endIndex + 1).forEach((r) => {
      if (r.subRows.length > 1) {
        r.getLeafRows().forEach((lr) => rangeIds.push(lr.original.id));
      } else {
        rangeIds.push(r.original.id);
      }
    });

    const idsToApply = targetState
      ? filterSelectableLeafIds(store, rangeIds)
      : rangeIds;
    if (targetState && idsToApply.length === 0) return;

    // Single atom write for the entire range
    setModel(applyRangeSelection(currentModel, idsToApply, targetState));

    setLastClickedRow({
      rowId: row.id,
      wasSelected: targetState,
    });
  };

  // Show tooltip only on unchecked checkbox cells when 3+ products are selected
  const shouldShowTooltip =
    selectedProductsCount >= 3 && checkedStatus === false;
  const cellContent = (
    <div
      className={cn(
        'flex w-full items-center justify-center p-2',
        (isOfferLocked || isCheckboxDisabled) && 'cursor-not-allowed opacity-50'
      )}
    >
      <Checkbox
        checked={checkedStatus}
        onClick={handleClick}
        tabIndex={-1}
        disabled={isOfferLocked || isCheckboxDisabled}
      />
    </div>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
        <TooltipContent side="right" align="center">
          <p>{t('kpi.tooltip_shift_click_message')}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return cellContent;
}

export default CheckboxCell;
